import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';

interface Business {
  id: number;
  business_id: string;
  business_name: string;
  business_email: string;
  phone_number: string;
  address: string;
  subscription_status: string;
  subscription_tier: string;
  allow_loan_checks?: boolean;
  allow_safety_checks?: boolean;
  allow_incidents?: boolean;
  allow_scan_sheets?: boolean;
  created_at?: string;
  updated_at?: string;
}

const BusinessManagement: React.FC = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  
  // Form state
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [subscriptionTier, setSubscriptionTier] = useState('');
  
  // Status and tier options
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const tierOptions = [
    { value: 'basic', label: 'Basic' },
    { value: 'standard', label: 'Standard' },
    { value: 'premium', label: 'Premium' },
    { value: 'enterprise', label: 'Enterprise' },
  ];

  const fetchBusinesses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/businesses`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Business API response:', response.data);
      setBusinesses(response.data);
    } catch (err: any) {
      console.error('Error fetching businesses:', err);
      setError('Failed to fetch businesses. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  // Filter by search and sort alphabetically by business name
  const filteredAndSortedBusinesses = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? businesses.filter(
          (b) =>
            (b.business_name || '').toLowerCase().includes(q) ||
            (b.business_email || '').toLowerCase().includes(q)
        )
      : businesses;
    return [...filtered].sort((a, b) =>
      (a.business_name || '').localeCompare(b.business_name || '', undefined, { sensitivity: 'base' })
    );
  }, [businesses, searchQuery]);
  
  const handleEditClick = (business: Business) => {
    setSelectedBusiness(business);
    // Set form values
    setBusinessName(business.business_name);
    setBusinessEmail(business.business_email);
    setPhoneNumber(business.phone_number);
    setAddress(business.address);
    setSubscriptionStatus(business.subscription_status);
    setSubscriptionTier(business.subscription_tier);
    setEditDialogOpen(true);
  };
  
  const handleDeleteClick = (business: Business) => {
    setSelectedBusiness(business);
    setDeleteDialogOpen(true);
  };
  
  const handleEditSubmit = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const response = await axios.put(
        `${API_BASE_URL}/businesses/${selectedBusiness.business_id}`, 
        {
          business_name: businessName,
          business_email: businessEmail,
          phone_number: phoneNumber,
          address: address,
          subscription_status: subscriptionStatus,
          subscription_tier: subscriptionTier,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      console.log('Business update response:', response.data);
      setEditDialogOpen(false);
      
      // Refresh the list
      fetchBusinesses();
    } catch (err: any) {
      console.error('Error updating business:', err);
      setError('Failed to update business. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      await axios.delete(`${API_BASE_URL}/businesses/${selectedBusiness.business_id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setDeleteDialogOpen(false);
      
      // Refresh the list
      fetchBusinesses();
    } catch (err: any) {
      console.error('Error deleting business:', err);
      setError('Failed to delete business. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && businesses.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', p: 3 }}> 
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
          Business Management
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h6">
            Manage Your Businesses
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search businesses by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/owner/add-business')}
            >
              Add New Business
            </Button>
          </Box>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: 'background.default' }}>
              <TableRow>
                <TableCell>Business Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedBusinesses.length > 0 ? (
                filteredAndSortedBusinesses.map((business) => (
                  <TableRow key={business.business_id}>
                    <TableCell>{business.business_name}</TableCell>
                    <TableCell>{business.business_email}</TableCell>
                    <TableCell>{business.phone_number}</TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'inline-block', 
                        px: 1.5, 
                        py: 0.5, 
                        borderRadius: 1,
                        bgcolor: 
                          !business.subscription_status ? 'grey.light' :
                          business.subscription_status === 'active' ? 'success.light' : 
                          business.subscription_status === 'pending' ? 'warning.light' : 'error.light',
                        color: 
                          !business.subscription_status ? 'text.secondary' :
                          business.subscription_status === 'active' ? 'success.dark' : 
                          business.subscription_status === 'pending' ? 'warning.dark' : 'error.dark',
                      }}>
                        {business.subscription_status ? business.subscription_status.charAt(0).toUpperCase() + business.subscription_status.slice(1) : 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>{business.subscription_tier ? business.subscription_tier.charAt(0).toUpperCase() + business.subscription_tier.slice(1) : 'Unknown'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Edit Business">
                          <IconButton 
                            color="primary" 
                            onClick={() => handleEditClick(business)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Settings">
                          <IconButton 
                            color="info" 
                            onClick={() => navigate(`/owner/business-settings/${business.business_id}`)}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Business">
                          <IconButton 
                            color="error" 
                            onClick={() => handleDeleteClick(business)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {businesses.length === 0
                      ? 'No businesses found. Click "Add New Business" to get started.'
                      : 'No businesses match your search. Try a different term.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Business</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Email"
                value={businessEmail}
                onChange={(e) => setBusinessEmail(e.target.value)}
                type="email"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                multiline
                rows={2}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Subscription Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={subscriptionStatus}
                  label="Subscription Status"
                  onChange={(e) => setSubscriptionStatus(e.target.value)}
                  required
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="tier-label">Subscription Tier</InputLabel>
                <Select
                  labelId="tier-label"
                  value={subscriptionTier}
                  label="Subscription Tier"
                  onChange={(e) => setSubscriptionTier(e.target.value)}
                  required
                >
                  {tierOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedBusiness?.business_name}</strong>?
            This action cannot be undone and will remove all data associated with this business.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete Business
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BusinessManagement; 